import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, API, getToken } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardList, CheckCircle2, Clock, PlayCircle, MapPin, User as UserIcon, Wrench, Camera, X, Loader2 } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";

const STATUS_META = {
  planned: { label: "Planifiée", bg: "bg-sky-50 text-sky-700 border-sky-200" },
  in_progress: { label: "En cours", bg: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { label: "Terminée", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Annulée", bg: "bg-gray-50 text-gray-600 border-gray-200" },
};
const PRIORITY_META = {
  low: { color: "#059669", label: "Faible" },
  medium: { color: "#D97706", label: "Moyenne" },
  high: { color: "#E60000", label: "Haute" },
  urgent: { color: "#7C2D12", label: "Urgente" },
};
const TYPE_META = { inspection: "Inspection", maintenance: "Maintenance", audit: "Audit", installation: "Installation", depannage: "Dépannage", livraison: "Livraison", autre: "Autre" };

export default function SalarieInterventions() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/salarie/qualiparc/interventions"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((it) => it.status === filter);

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/salarie/qualiparc/interventions/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      if (detail?.id === id) setDetail(data);
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleChecklist = async (interv, idx) => {
    const cl = [...(interv.checklist || [])];
    cl[idx] = { ...cl[idx], done: !cl[idx].done };
    try {
      const { data } = await api.patch(`/salarie/qualiparc/interventions/${interv.id}`, { checklist: cl });
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      if (detail?.id === data.id) setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNotes = async () => {
    if (!detail) return;
    try {
      const { data } = await api.patch(`/salarie/qualiparc/interventions/${detail.id}`, { notes });
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      setDetail(data); toast.success("Notes enregistrées");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadPhoto = async (file, checklistItemId = null) => {
    if (!detail || !file) return;
    if (!file.type.startsWith("image/")) { toast.error("Fichier image attendu"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo > 8 Mo"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = checklistItemId ? `?checklist_item_id=${encodeURIComponent(checklistItemId)}` : "";
      const res = await fetch(`${API}/salarie/qualiparc/interventions/${detail.id}/photos${qs}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const photo = await res.json();
      const updated = { ...detail, photos: [...(detail.photos || []), photo] };
      setDetail(updated);
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error("Erreur upload");
    } finally { setUploading(false); }
  };

  const deletePhoto = async (photoId) => {
    if (!detail) return;
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/qualiparc/interventions/${detail.id}/photos/${photoId}`);
      const updated = { ...detail, photos: (detail.photos || []).filter((p) => p.id !== photoId) };
      setDetail(updated);
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="QualiParc" title="Mes interventions" sub="Interventions terrain qui vous ont été assignées." />
      <div className="p-6 lg:p-10">
        <div className="flex items-center gap-3 mb-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 h-9" data-testid="sal-int-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <div className="border border-[#DEE2E6] bg-white p-10 text-center text-sm text-[#868E96]" data-testid="sal-int-empty">
            <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Aucune intervention assignée pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((it) => {
              const st = STATUS_META[it.status] || STATUS_META.planned;
              const pr = PRIORITY_META[it.priority] || PRIORITY_META.medium;
              const clDone = (it.checklist || []).filter((c) => c.done).length;
              const clTotal = (it.checklist || []).length;
              return (
                <div key={it.id} className="border border-[#DEE2E6] bg-white p-4" data-testid={`sal-int-card-${it.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border font-bold ${st.bg}`}>{st.label}</span>
                    <span className="text-[10px] uppercase font-bold" style={{ color: pr.color }}>● {pr.label}</span>
                    <span className="text-[10px] uppercase text-[#868E96]">{TYPE_META[it.type]}</span>
                  </div>
                  <h3 className="font-display font-bold mt-2 truncate">{it.title}</h3>
                  <div className="text-xs text-[#495057] mt-2 grid grid-cols-1 gap-1">
                    <div><Clock className="h-3 w-3 inline mr-1" /> {new Date(it.scheduled_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {it.duration_minutes} min</div>
                    {(it.site_label || it.address) && <div className="truncate"><MapPin className="h-3 w-3 inline mr-1" /> {[it.site_label, it.address].filter(Boolean).join(" — ")}</div>}
                  </div>
                  {clTotal > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-1">Checklist ({clDone}/{clTotal})</div>
                      <div className="w-full bg-[#F1F3F5] h-1.5"><div className="bg-[#059669] h-1.5" style={{ width: `${clTotal ? Math.round((clDone / clTotal) * 100) : 0}%` }} /></div>
                    </div>
                  )}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {it.status === "planned" && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => changeStatus(it.id, "in_progress")} data-testid={`sal-int-start-${it.id}`}><PlayCircle className="h-3 w-3 mr-1" /> Démarrer</Button>}
                    {it.status !== "completed" && it.status !== "cancelled" && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => changeStatus(it.id, "completed")} data-testid={`sal-int-complete-${it.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Terminer</Button>}
                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setDetail(it); setNotes(it.notes || ""); }} data-testid={`sal-int-open-${it.id}`}>Ouvrir →</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4" /> {detail.title}</DialogTitle>
                <DialogDescription>{TYPE_META[detail.type]} · {STATUS_META[detail.status]?.label}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {detail.description && <p className="text-sm text-[#495057]">{detail.description}</p>}
                <div className="text-xs text-[#495057] space-y-1">
                  <div><Clock className="h-3 w-3 inline mr-1" /> {new Date(detail.scheduled_at).toLocaleString("fr-FR")}</div>
                  {(detail.site_label || detail.address) && <div><MapPin className="h-3 w-3 inline mr-1" /> {[detail.site_label, detail.address].filter(Boolean).join(" — ")}</div>}
                </div>
                {(detail.checklist || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-2">Checklist</div>
                    <div className="space-y-1">
                      {detail.checklist.map((c, idx) => (
                        <button key={c.id || idx} type="button" data-testid={`sal-int-cl-${idx}`} onClick={() => toggleChecklist(detail, idx)} className={`w-full text-left flex items-center gap-2 p-2 border ${c.done ? "border-emerald-300 bg-emerald-50" : "border-[#DEE2E6] bg-white hover:bg-[#F8F9FA]"}`}>
                          <CheckCircle2 className={`h-4 w-4 ${c.done ? "text-emerald-600" : "text-[#DEE2E6]"}`} />
                          <span className={`text-sm ${c.done ? "line-through text-[#868E96]" : ""}`}>{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-1">Notes terrain</div>
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sal-int-notes" placeholder="Vos observations…" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold">Photos ({(detail.photos || []).length})</div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      data-testid="sal-int-photo-input"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }}
                    />
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sal-int-photo-btn">
                      {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Camera className="h-3 w-3 mr-1" />}
                      {uploading ? "Envoi…" : "Prendre / Ajouter photo"}
                    </Button>
                  </div>
                  {(detail.photos || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {detail.photos.map((p) => (
                        <div key={p.id} className="relative aspect-square bg-[#F1F3F5] border border-[#DEE2E6] overflow-hidden group" data-testid={`sal-int-photo-${p.id}`}>
                          <AuthImage
                            path={`/qualiparc/interventions/${detail.id}/photos/${p.id}`}
                            alt="Photo terrain"
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => deletePhoto(p.id)}
                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                            data-testid={`sal-int-photo-del-${p.id}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Fermer</Button>
                <Button onClick={saveNotes} className="bg-[#002FA7]" data-testid="sal-int-save-notes">Enregistrer notes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
